import { useState, useEffect, useRef } from 'react';
import GameHUD from './comLam/GameHUD';
import KnowledgePopup from './comLam/KnowledgePopup';
import KnowledgeTest from './comLam/KnowledgeTest';
import Notebook from './comLam/Notebook';
import SummaryScreen from './comLam/SummaryScreen';
import SafetyWarning from './comLam/SafetyWarning';
import { KNOWLEDGE_LESSONS } from './comLam/types';
import { Round0ChonDungCu, Round1RuaTay } from './comLam/RoundsEarly';
import { Round1ChonNguyenLieu } from './comLam/RoundsNew';
import { Round2RuaDungCu, Round3VoGao, Round4ChuanBiOng } from './comLam/RoundsMid';
import { Round5NhomLua, Round6NuongCom, Round7XoayOng, Round8HoanThanh } from './comLam/RoundsLate';

const TOTAL_ROUNDS = 10;
const ROUND_LESSON_IDS = [0, 1, 3, 4, 5, 6, 7, 8, 9, 10];

const MUSIC_URL = 'https://storage.readdy-site.link/project_files/d941aef1-6212-4727-9a5e-0c38b2f72f78/8966e215-023f-44c1-977c-b1c73b32b111_chillrightsmusic-strawberry-358303.mp3?v=7b527f66979d2c7f7ce48379f84a6e75';

function useBgMusic(playing: boolean) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(MUSIC_URL);
    audio.loop = true;
    audio.volume = 0.35;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [playing]);
}

export default function ComLamGame({ onBack }: { onBack: () => void }) {
  const [showSafetyWarning, setShowSafetyWarning] = useState(true);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [cleanBar, setCleanBar] = useState(10);
  const [bacteriaBar, setBacteriaBar] = useState(70);
  const [cookedBar, setCookedBar] = useState(0);
  const [unlockedLessons, setUnlockedLessons] = useState<number[]>([]);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [showNotebook, setShowNotebook] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [showKnowledgeTest, setShowKnowledgeTest] = useState(false);
  const [testScore, setTestScore] = useState(0);
  const [roundsDone, setRoundsDone] = useState(0);
  const [musicPlaying, setMusicPlaying] = useState(true);

  useBgMusic(musicPlaying && !gameFinished && !showSafetyWarning);

  const handleRoundComplete = (_success: boolean, points: number) => {
    setScore(s => s + points);
    setRoundsDone(r => r + 1);
    const lessonId = ROUND_LESSON_IDS[round];
    if (!unlockedLessons.includes(lessonId)) {
      setUnlockedLessons(prev => [...prev, lessonId]);
    }
    setShowKnowledge(true);
  };

  const handleKnowledgeContinue = () => {
    setShowKnowledge(false);
    const nextRound = round + 1;
    if (nextRound >= TOTAL_ROUNDS) {
      setShowKnowledgeTest(true);
    } else {
      setRound(nextRound);
    }
  };

  const handleTestFinish = (ts: number) => {
    setTestScore(ts);
    setScore(s => s + ts);
    setShowKnowledgeTest(false);
    setGameFinished(true);
  };

  const sharedProps = {
    onComplete: handleRoundComplete,
    bacteriaLevel: bacteriaBar,
    setBacteriaLevel: setBacteriaBar,
    cleanLevel: cleanBar,
    setCleanLevel: setCleanBar,
  };

  if (showSafetyWarning) return <SafetyWarning onStart={() => setShowSafetyWarning(false)} />;

  if (showKnowledgeTest) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 bg-white border-b border-amber-100">
          <h2 className="text-xl font-extrabold text-amber-700">📝 Bài kiểm tra an toàn thực phẩm</h2>
          <span className="text-base text-amber-500 font-semibold">Hoàn thành để nhận kết quả!</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-start px-4 py-6">
          <div className="w-full max-w-2xl">
            <KnowledgeTest onFinish={handleTestFinish} />
          </div>
        </div>
      </div>
    );
  }

  if (gameFinished) {
    return (
      <SummaryScreen
        score={score}
        cleanBar={cleanBar}
        bacteriaBar={bacteriaBar}
        cookedBar={cookedBar}
        roundsCompleted={roundsDone}
        testScore={testScore}
        onBack={onBack}
        onRestart={() => {
          setRound(0); setScore(0); setCleanBar(10); setBacteriaBar(70);
          setCookedBar(0); setUnlockedLessons([]); setGameFinished(false);
          setRoundsDone(0); setShowKnowledgeTest(false); setTestScore(0); setShowSafetyWarning(true);
        }}
      />
    );
  }

  const roundTitles = [
    '🍳 Công đoạn 1: Chọn dụng cụ nấu ăn',
    '🛒 Công đoạn 2: Chọn nguyên liệu sạch',
    '🙌 Công đoạn 3: Rửa tay (7 bước)',
    '🎋 Công đoạn 4: Rửa dụng cụ và ống nứa',
    '🍚 Công đoạn 5: Vo gạo sạch',
    '🎍 Công đoạn 6: Chuẩn bị ống nứa',
    '🔥 Công đoạn 7: Nhóm lửa (người lớn giúp)',
    '🌡️ Công đoạn 8: Nướng cơm – giữ lửa',
    '🔄 Công đoạn 9: Xoay ống cơm',
    '🎉 Công đoạn 10: Hoàn thành!',
  ];

  const currentLesson = KNOWLEDGE_LESSONS[ROUND_LESSON_IDS[round]];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col">
      <GameHUD
        score={score}
        round={round}
        totalRounds={TOTAL_ROUNDS}
        cleanBar={cleanBar}
        bacteriaBar={bacteriaBar}
        cookedBar={cookedBar}
        onNotebook={() => setShowNotebook(true)}
      />
      <div className="flex justify-end px-4 pt-1">
        <button onClick={() => setMusicPlaying(v => !v)}
          className="text-sm text-amber-600 flex items-center gap-1.5 cursor-pointer hover:text-amber-800 transition-all">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={musicPlaying ? 'ri-volume-up-line' : 'ri-volume-mute-line'}></i>
          </div>
          {musicPlaying ? 'Tắt nhạc' : 'Bật nhạc'}
        </button>
      </div>
      {bacteriaBar > 70 && (
        <div className="mx-4 mt-2 p-3 bg-red-100 border border-red-300 rounded-2xl flex items-center gap-2">
          <span className="text-xl">🦠</span>
          <p className="text-sm font-bold text-red-700">Vi khuẩn cao quá! Hãy giữ vệ sinh cẩn thận hơn!</p>
        </div>
      )}
      <div className="flex-1 flex flex-col items-center justify-start px-3 py-3">
        <div className="w-full max-w-2xl">
          <h2 className="text-center text-base font-extrabold text-amber-700 mb-3 bg-amber-100 rounded-2xl py-3 px-4">
            {roundTitles[round]}
          </h2>
          {round === 0 && <Round0ChonDungCu {...sharedProps} />}
          {round === 1 && <Round1ChonNguyenLieu {...sharedProps} />}
          {round === 2 && <Round1RuaTay {...sharedProps} />}
          {round === 3 && <Round2RuaDungCu {...sharedProps} />}
          {round === 4 && <Round3VoGao {...sharedProps} />}
          {round === 5 && <Round4ChuanBiOng {...sharedProps} />}
          {round === 6 && <Round5NhomLua {...sharedProps} />}
          {round === 7 && (
            <Round6NuongCom {...sharedProps} cookedLevel={cookedBar} setCookedLevel={setCookedBar} />
          )}
          {round === 8 && <Round7XoayOng {...sharedProps} />}
          {round === 9 && <Round8HoanThanh {...sharedProps} />}
        </div>
      </div>
      {showKnowledge && currentLesson && (
        <KnowledgePopup lesson={currentLesson} onContinue={handleKnowledgeContinue} />
      )}
      {showNotebook && (
        <Notebook unlockedIds={unlockedLessons} onClose={() => setShowNotebook(false)} />
      )}
    </div>
  );
}
